/**
 * Utility functions for bug reporting
 */
import html2canvas from 'html2canvas';

export interface ConsoleLog {
  level: string;
  message: string;
  timestamp: number;
  stack?: string;
}

export interface NetworkLog {
  url: string;
  method: string;
  status?: number;
  statusText?: string;
  requestHeaders?: Record<string, string>;
  responseHeaders?: Record<string, string>;
  requestBody?: any;
  responseBody?: any;
  timestamp: number;
  duration?: number;
}

// Global log storage
if (typeof window !== 'undefined') {
  (window as any).__bugReportLogs = (window as any).__bugReportLogs || [];
  (window as any).__globalErrors = (window as any).__globalErrors || [];
}

/**
 * Initialize console log interception
 * Call this early in the app lifecycle
 */
export function initializeConsoleLogCollection() {
  if (typeof window === 'undefined' || !(window as any).console) {
    return;
  }

  const logs = (window as any).__bugReportLogs || [];
  const maxLogs = 100;

  const addLog = (level: string, args: any[]) => {
    const message = args
      .map((arg) => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');

    logs.push({
      level,
      message,
      timestamp: Date.now(),
    });

    // Keep only the most recent logs
    if (logs.length > maxLogs) {
      logs.shift();
    }
  };

  // Intercept console methods
  const originalLog = console.log;
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalInfo = console.info;
  const originalDebug = console.debug;

  console.log = (...args: any[]) => {
    addLog('log', args);
    originalLog.apply(console, args);
  };

  console.error = (...args: any[]) => {
    addLog('error', args);
    originalError.apply(console, args);
  };

  console.warn = (...args: any[]) => {
    addLog('warn', args);
    originalWarn.apply(console, args);
  };

  console.info = (...args: any[]) => {
    addLog('info', args);
    originalInfo.apply(console, args);
  };

  console.debug = (...args: any[]) => {
    addLog('debug', args);
    originalDebug.apply(console, args);
  };

  // Store logs array
  (window as any).__bugReportLogs = logs;
}

/**
 * Collect console logs from the browser
 * Safe to call even during crashes
 */
export function collectConsoleLogs(): ConsoleLog[] {
  try {
    if (typeof window === 'undefined') {
      return [];
    }

    const logs = (window as any).__bugReportLogs || [];
    return [...logs];
  } catch (error) {
    console.error('Error collecting console logs:', error);
    return [];
  }
}

/**
 * Collect network logs from Performance API
 */
export function collectNetworkLogs(): NetworkLog[] {
  try {
    const logs: NetworkLog[] = [];

    if (typeof window !== 'undefined' && window.performance) {
      try {
        const entries = window.performance.getEntriesByType(
          'resource'
        ) as PerformanceResourceTiming[];

        entries.forEach((entry) => {
          const resourceEntry = entry as any;
          logs.push({
            url: entry.name,
            method: resourceEntry.requestMethod || 'GET',
            status: resourceEntry.responseStatus,
            timestamp: entry.startTime,
            duration: entry.duration,
          });
        });
      } catch (error) {
        console.error('Failed to collect network logs:', error);
      }
    }

    return logs;
  } catch (error) {
    console.error('Error collecting network logs:', error);
    return [];
  }
}

/**
 * Find the main scrollable container and the actual scrollable content element
 */
function findScrollableContainer(): {
  container: HTMLElement;
  contentElement: HTMLElement | null;
} {
  // Check common scrollable containers
  const body = document.body;
  const html = document.documentElement;

  // Look for main content containers that might be scrollable first
  const mainContent =
    document.querySelector('main') ||
    document.querySelector('[role="main"]') ||
    document.querySelector('.main-content') ||
    document.querySelector('[class*="content"]') ||
    document.querySelector('#root > div > div');

  if (mainContent && mainContent instanceof HTMLElement) {
    if (mainContent.scrollHeight > mainContent.clientHeight) {
      return { container: mainContent, contentElement: mainContent };
    }
  }

  // Check if body is scrollable
  if (body.scrollHeight > body.clientHeight) {
    // Try to find the actual content element inside body
    const bodyChildren = Array.from(body.children) as HTMLElement[];
    for (const child of bodyChildren) {
      if (child.scrollHeight > child.clientHeight && child.scrollHeight > 0) {
        return { container: body, contentElement: child };
      }
    }
    return { container: body, contentElement: null };
  }

  // Check if html is scrollable
  if (html.scrollHeight > html.clientHeight) {
    return { container: html, contentElement: body };
  }

  // Default to body
  return { container: body, contentElement: null };
}

/**
 * Function to check if an element is part of the bug report dialog
 */
function isBugReportElement(element: Element): boolean {
  // Check if element has the bug report dialog attribute
  if (element.hasAttribute('data-bug-report-dialog')) {
    console.log('[Bug Report] Ignoring element (has attribute):', {
      tag: element.tagName,
      classes: element.className,
    });
    return true;
  }

  // Check if element has the bug report backdrop attribute (added by BugReportDialog)
  if (element.hasAttribute('data-bug-report-backdrop')) {
    console.log('[Bug Report] Ignoring backdrop (has backdrop attribute):', {
      tag: element.tagName,
      classes: element.className,
    });
    return true;
  }

  // Check if this is an overlay that's a sibling of the bug report dialog
  // DialogOverlay and DialogContent are siblings in the same portal
  if (element.hasAttribute('data-radix-dialog-overlay')) {
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      // Check if there's a bug report dialog in the same portal (as a sibling)
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        console.log(
          '[Bug Report] Ignoring overlay (sibling of bug report dialog):',
          {
            tag: element.tagName,
            classes: element.className,
          }
        );
        return true;
      }
    }
  }

  // Also check if element matches backdrop pattern (fixed, inset-0, backdrop-blur)
  // and is in a portal with bug report dialog
  const classList = element.classList;
  const isBackdropPattern =
    classList.contains('fixed') &&
    classList.contains('inset-0') &&
    (classList.contains('backdrop-blur') ||
      classList.contains('backdrop-blur-sm') ||
      element.className.includes('backdrop-blur') ||
      element.className.includes('bg-background/80') ||
      element.className.includes('bg-black/80'));

  if (isBackdropPattern) {
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        console.log(
          '[Bug Report] Ignoring backdrop (matches pattern in bug report portal):',
          {
            tag: element.tagName,
            classes: element.className,
          }
        );
        return true;
      }
    }
  }

  // Check if element is in a portal that contains the bug report dialog
  // If so, ignore ALL elements in that portal (overlay, backdrop, content, etc.)
  const portal = element.closest('[data-radix-portal]');
  if (portal) {
    const hasBugReportDialog = portal.querySelector(
      '[data-bug-report-dialog="true"]'
    );
    if (hasBugReportDialog) {
      // Ignore ALL elements in the bug report portal
      // This includes overlay, backdrop, content, and any other children
      console.log('[Bug Report] Ignoring element (in bug report portal):', {
        tag: element.tagName,
        classes: element.className,
        hasOverlayAttr: element.hasAttribute('data-radix-dialog-overlay'),
      });
      return true;
    }
  }

  return false;
}

/**
 * Handle hiding bug report dialog elements in cloned document
 */
function handleBugReportDialogHiding(clonedDoc: Document): void {
  // Hide ONLY the bug report dialog and its backdrop in the cloned document
  // This prevents capturing the bug report dialog itself, but keeps all other dialogs visible
  // IMPORTANT: All other dialogs (delete confirmations, alerts, etc.) should remain visible
  // We identify the bug report dialog by its unique data-bug-report-dialog="true" attribute

  console.log(
    '[Bug Report] Starting onclone - hiding bug report dialog elements'
  );

  // Find and hide the bug report dialog specifically by its data attribute
  // This is the ONLY dialog we want to hide - all others stay visible
  const bugReportDialogs = clonedDoc.querySelectorAll(
    '[data-bug-report-dialog="true"]'
  );
  console.log(
    '[Bug Report] Found',
    bugReportDialogs.length,
    'bug report dialog(s)'
  );
  bugReportDialogs.forEach((dialog) => {
    const element = dialog as HTMLElement;
    element.style.display = 'none';
    element.style.visibility = 'hidden';
    element.style.opacity = '0';
  });

  // Find and hide the bug report backdrop by its data attribute
  const bugReportBackdrops = clonedDoc.querySelectorAll(
    '[data-bug-report-backdrop="true"]'
  );
  console.log(
    '[Bug Report] Found',
    bugReportBackdrops.length,
    'bug report backdrop(s) by attribute'
  );
  bugReportBackdrops.forEach((backdrop) => {
    const element = backdrop as HTMLElement;
    console.log('[Bug Report] Hiding backdrop (by attribute):', {
      tag: element.tagName,
      classes: element.className,
    });
    element.style.display = 'none';
    element.style.visibility = 'hidden';
    element.style.opacity = '0';
    element.style.pointerEvents = 'none';
    element.style.position = 'absolute';
    element.style.top = '-9999px';
    element.style.left = '-9999px';
    element.style.width = '0';
    element.style.height = '0';
    element.style.zIndex = '-1';
  });

  // Fallback: Find all overlays in portals that contain bug report dialogs
  // This catches the backdrop even if ignoreElements didn't catch it or attribute wasn't copied
  const allOverlays = clonedDoc.querySelectorAll('[data-radix-dialog-overlay]');
  console.log(
    '[Bug Report] Found',
    allOverlays.length,
    'overlay(s) total in cloned doc'
  );
  allOverlays.forEach((overlay) => {
    const element = overlay as HTMLElement;
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        // This overlay is in the bug report portal, hide it
        console.log(
          '[Bug Report] Hiding backdrop (fallback - overlay in portal):',
          {
            tag: element.tagName,
            classes: element.className,
            hasBackdropAttr: element.hasAttribute('data-bug-report-backdrop'),
          }
        );
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.pointerEvents = 'none';
        element.style.position = 'absolute';
        element.style.top = '-9999px';
        element.style.left = '-9999px';
        element.style.width = '0';
        element.style.height = '0';
        element.style.zIndex = '-1';
      }
    }
  });

  // Find the bug report dialog's portal and hide it completely
  // This will hide the entire portal including overlay, content, and backdrop
  const portals = clonedDoc.querySelectorAll('[data-radix-portal]');
  console.log('[Bug Report] Found', portals.length, 'portal(s)');
  portals.forEach((portal) => {
    const element = portal as HTMLElement;
    // Check if this portal contains the bug report dialog
    const hasBugReportDialog = element.querySelector(
      '[data-bug-report-dialog="true"]'
    );
    if (hasBugReportDialog) {
      console.log('[Bug Report] Hiding bug report portal and all children');
      // Hide the entire portal - this includes overlay, content, and all children
      element.style.display = 'none';
      element.style.visibility = 'hidden';
      element.style.opacity = '0';
      element.style.pointerEvents = 'none';
      element.style.position = 'absolute';
      element.style.top = '-9999px';
      element.style.left = '-9999px';
      element.style.width = '0';
      element.style.height = '0';
      // Also hide all children explicitly with multiple methods
      const allChildren = element.querySelectorAll('*');
      console.log(
        '[Bug Report] Hiding',
        allChildren.length,
        'children in portal'
      );
      allChildren.forEach((child) => {
        const childEl = child as HTMLElement;
        const tagName = childEl.tagName;
        const classes = childEl.className;
        const hasOverlayAttr = childEl.hasAttribute(
          'data-radix-dialog-overlay'
        );

        // Log backdrop elements for debugging
        if (
          hasOverlayAttr ||
          classes.includes('backdrop-blur') ||
          classes.includes('inset-0')
        ) {
          console.log('[Bug Report] Hiding backdrop element:', {
            tag: tagName,
            classes: classes,
            hasOverlayAttr: hasOverlayAttr,
          });
        }

        childEl.style.display = 'none';
        childEl.style.visibility = 'hidden';
        childEl.style.opacity = '0';
        childEl.style.pointerEvents = 'none';
        // Also set dimensions to 0 to ensure it's completely hidden
        childEl.style.width = '0';
        childEl.style.height = '0';
        childEl.style.position = 'absolute';
        childEl.style.top = '-9999px';
        childEl.style.left = '-9999px';
      });
    }
  });

  // Hide the bug report dialog's overlay/backdrop specifically
  // We need to find overlays that are siblings or parents of the bug report dialog
  const overlays = clonedDoc.querySelectorAll('[data-radix-dialog-overlay]');
  console.log('[Bug Report] Found', overlays.length, 'overlay(s)');
  overlays.forEach((overlay) => {
    const element = overlay as HTMLElement;
    // Check if this overlay is associated with the bug report dialog
    // The overlay is typically a sibling in the same portal
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        console.log('[Bug Report] Hiding overlay:', {
          classes: element.className,
          zIndex: element.style.zIndex,
        });
        // Hide completely with multiple methods
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.pointerEvents = 'none';
        element.style.position = 'absolute';
        element.style.top = '-9999px';
        element.style.left = '-9999px';
        element.style.width = '0';
        element.style.height = '0';
        element.style.zIndex = '-1';
      }
    }
  });

  // Also hide any fixed inset-0 elements that could be backdrops in bug report portal
  const allFixedInset0 = clonedDoc.querySelectorAll('.fixed.inset-0');
  allFixedInset0.forEach((el) => {
    const element = el as HTMLElement;
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        // This is likely the backdrop, hide it completely
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.pointerEvents = 'none';
        element.style.position = 'absolute';
        element.style.top = '-9999px';
        element.style.left = '-9999px';
        element.style.width = '0';
        element.style.height = '0';
        element.style.zIndex = '-1';
      }
    }
  });

  // Hide any element that looks like a backdrop in the bug report portal
  // Check all fixed elements with backdrop styling
  const allElements = clonedDoc.querySelectorAll('*');
  allElements.forEach((el) => {
    const element = el as HTMLElement;
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        // Check if it looks like a backdrop (fixed, inset-0, backdrop-blur)
        const classList = element.classList;
        const inlineZIndex = element.style.zIndex;
        const hasBackdropClasses =
          classList.contains('backdrop-blur') ||
          classList.contains('backdrop-blur-sm') ||
          classList.contains('bg-background/80') ||
          classList.contains('bg-black/80');

        const isFixedOverlay =
          classList.contains('fixed') &&
          classList.contains('inset-0') &&
          (hasBackdropClasses ||
            inlineZIndex === '9998' ||
            inlineZIndex === '9999' ||
            inlineZIndex === '50' ||
            element.getAttribute('data-radix-dialog-overlay'));

        if (isFixedOverlay) {
          // This is definitely the backdrop, hide it completely
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          element.style.pointerEvents = 'none';
          element.style.position = 'absolute';
          element.style.top = '-9999px';
          element.style.left = '-9999px';
          element.style.width = '0';
          element.style.height = '0';
          element.style.zIndex = '-1';
        }
      }
    }
  });

  // Hide role="dialog" elements that are part of the bug report dialog
  // IMPORTANT: Only hide dialogs that are part of the bug report dialog
  // All other dialogs (delete confirmations, alerts, etc.) remain visible
  const dialogs = clonedDoc.querySelectorAll('[role="dialog"]');
  dialogs.forEach((dialog) => {
    const element = dialog as HTMLElement;
    // Only hide if this dialog has the bug report data attribute or is in the same portal as bug report
    // This ensures other dialogs (like delete confirmations) are NOT hidden
    const hasBugReportAttr = element.hasAttribute('data-bug-report-dialog');
    const portal = element.closest('[data-radix-portal]');
    if (
      hasBugReportAttr ||
      (portal && portal.querySelector('[data-bug-report-dialog="true"]'))
    ) {
      element.style.display = 'none';
      element.style.visibility = 'hidden';
      element.style.opacity = '0';
    }
    // All other dialogs without the bug report attribute remain visible
  });

  // Hide fixed position elements that are part of the bug report dialog
  // Only hide if they're in the same portal as the bug report dialog
  const fixedElements = clonedDoc.querySelectorAll('.fixed');
  fixedElements.forEach((el) => {
    const element = el as HTMLElement;
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        const classList = element.classList;
        // Check if it matches dialog overlay pattern
        const isDialogOverlay =
          classList.contains('inset-0') &&
          (classList.contains('z-50') ||
            element.style.zIndex === '50' ||
            element.style.zIndex === '9999' ||
            element.style.zIndex === '9998') &&
          (classList.contains('backdrop-blur') ||
            classList.contains('backdrop-blur-sm') ||
            classList.contains('bg-background/80') ||
            classList.contains('bg-black/80') ||
            element.getAttribute('data-radix-dialog-overlay'));

        if (isDialogOverlay) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          element.style.pointerEvents = 'none';
          element.style.position = 'absolute';
        }
      }
    }
  });

  // Also hide any backdrop-blur elements that are in the bug report dialog portal
  const backdropBlurElements = clonedDoc.querySelectorAll(
    '.backdrop-blur, .backdrop-blur-sm, [class*="backdrop"]'
  );
  backdropBlurElements.forEach((el) => {
    const element = el as HTMLElement;
    const portal = element.closest('[data-radix-portal]');
    if (portal) {
      const hasBugReportDialog = portal.querySelector(
        '[data-bug-report-dialog="true"]'
      );
      if (hasBugReportDialog) {
        // This is a backdrop element in the bug report dialog portal, hide it
        element.style.display = 'none';
        element.style.visibility = 'hidden';
        element.style.opacity = '0';
        element.style.pointerEvents = 'none';
        element.style.position = 'absolute';
        element.style.top = '-9999px';
        element.style.left = '-9999px';
      }
    }
  });

  // Also hide any elements with bg-background/80 or bg-black/80 that are overlays
  const bgOverlayElements = clonedDoc.querySelectorAll(
    '[class*="bg-background/80"], [class*="bg-black/80"]'
  );
  bgOverlayElements.forEach((el) => {
    const element = el as HTMLElement;
    if (
      element.classList.contains('fixed') &&
      element.classList.contains('inset-0')
    ) {
      const portal = element.closest('[data-radix-portal]');
      if (portal) {
        const hasBugReportDialog = portal.querySelector(
          '[data-bug-report-dialog="true"]'
        );
        if (hasBugReportDialog) {
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          element.style.pointerEvents = 'none';
        }
      }
    }
  });

  // Ensure all images are loaded in the cloned document
  const images = clonedDoc.querySelectorAll('img');
  images.forEach((img) => {
    if (!img.complete) {
      (img as HTMLImageElement).src = (img as HTMLImageElement).src;
    }
  });
  // Force font rendering improvements
  const style = clonedDoc.createElement('style');
  style.textContent = `
          * {
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
          }
        `;
  clonedDoc.head.appendChild(style);
}

/**
 * Internal function to capture screenshot with loading indicator
 */
async function captureScreenshotInternal(
  checkDialog: boolean
): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('Window is not available');
  }

  // Create loading indicator
  const loadingIndicator = document.createElement('div');
  loadingIndicator.setAttribute('data-bug-report-dialog', 'true'); // Ignored by isBugReportElement
  loadingIndicator.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: white;
    padding: 10px 20px;
    border-radius: 20px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    font-size: 14px;
    font-weight: 500;
    z-index: 2147483647;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: none;
    transition: opacity 0.2s ease;
  `;

  // Add spinner SVG
  loadingIndicator.innerHTML = `
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style="animation: bug-report-spin 1s linear infinite;">
      <style>
        @keyframes bug-report-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      </style>
      <circle opacity="0.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path opacity="0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
    <span>Capturing screenshot...</span>
  `;

  document.body.appendChild(loadingIndicator);

  // Force reflow and wait for render
  void loadingIndicator.offsetHeight;
  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  );

  try {
    return await performScreenshotCapture(checkDialog);
  } finally {
    if (loadingIndicator && loadingIndicator.parentNode) {
      loadingIndicator.parentNode.removeChild(loadingIndicator);
    }
  }
}

/**
 * Actual implementation of screenshot capture
 * @param checkDialog - Whether to check for dialog and mark backdrop
 */
async function performScreenshotCapture(checkDialog: boolean): Promise<string> {
  if (typeof window === 'undefined') {
    throw new Error('Window is not available');
  }

  // Mark the backdrop overlay right before screenshot capture (only if dialog exists)
  if (checkDialog) {
    const dialogContent = document.querySelector(
      '[data-bug-report-dialog="true"]'
    );
    if (dialogContent) {
      // Try multiple methods to find the backdrop
      let backdropFound = false;

      // Method 1: Find portal and look for overlay inside
      const portal = dialogContent.closest('[data-radix-portal]');
      if (portal) {
        const overlay = portal.querySelector('[data-radix-dialog-overlay]');
        if (overlay) {
          (overlay as HTMLElement).setAttribute(
            'data-bug-report-backdrop',
            'true'
          );
          console.log('✅ Marked bug report backdrop (via portal)');
          backdropFound = true;
        }
      }

      // Method 2: Find all overlays and check if they're related to bug report dialog
      if (!backdropFound) {
        const allOverlays = document.querySelectorAll(
          '[data-radix-dialog-overlay]'
        );
        console.log(
          `[Bug Report] Found ${allOverlays.length} overlay(s) to check`
        );
        allOverlays.forEach((overlay, index) => {
          const overlayEl = overlay as HTMLElement;
          console.log(`[Bug Report] Checking overlay ${index}:`, {
            tag: overlayEl.tagName,
            classes: overlayEl.className.substring(0, 100),
            parent: overlayEl.parentElement?.tagName,
            hasPortal: !!overlayEl.closest('[data-radix-portal]'),
          });

          // Check if overlay is a sibling of dialog content (same parent)
          if (overlay.parentElement === dialogContent.parentElement) {
            overlayEl.setAttribute('data-bug-report-backdrop', 'true');
            console.log(
              '✅ Marked bug report backdrop (sibling of dialog content)'
            );
            console.log(
              '✅ Verifying attribute:',
              overlayEl.hasAttribute('data-bug-report-backdrop')
            );
            backdropFound = true;
            return;
          }

          // Check if overlay is a sibling of the portal (same parent as portal)
          if (portal && overlay.parentElement === portal.parentElement) {
            overlayEl.setAttribute('data-bug-report-backdrop', 'true');
            console.log('✅ Marked bug report backdrop (sibling of portal)');
            console.log(
              '✅ Verifying attribute:',
              overlayEl.hasAttribute('data-bug-report-backdrop')
            );
            backdropFound = true;
            return;
          }

          // Check if overlay is in the same portal (if portal exists)
          if (portal) {
            const overlayPortal = overlay.closest('[data-radix-portal]');
            if (overlayPortal === portal) {
              overlayEl.setAttribute('data-bug-report-backdrop', 'true');
              console.log('✅ Marked bug report backdrop (in same portal)');
              console.log(
                '✅ Verifying attribute:',
                overlayEl.hasAttribute('data-bug-report-backdrop')
              );
              backdropFound = true;
              return;
            }
          }

          // Check if overlay is next to the dialog content in DOM order
          // (overlay might be rendered before or after the dialog content)
          const dialogRect = dialogContent.getBoundingClientRect();
          const overlayRect = overlayEl.getBoundingClientRect();
          // Check if overlay covers the dialog (backdrop should cover the entire screen)
          if (
            overlayRect.top <= dialogRect.top &&
            overlayRect.left <= dialogRect.left &&
            overlayRect.bottom >= dialogRect.bottom &&
            overlayRect.right >= dialogRect.right &&
            overlayRect.width >= dialogRect.width &&
            overlayRect.height >= dialogRect.height
          ) {
            // This overlay covers the dialog, likely the backdrop
            overlayEl.setAttribute('data-bug-report-backdrop', 'true');
            console.log('✅ Marked bug report backdrop (covers dialog)');
            console.log(
              '✅ Verifying attribute:',
              overlayEl.hasAttribute('data-bug-report-backdrop')
            );
            backdropFound = true;
            return;
          }
        });
      }

      // Method 3: Find backdrop by class pattern (fixed, inset-0, backdrop-blur)
      // This catches backdrops that don't have data-radix-dialog-overlay attribute
      if (!backdropFound) {
        const allFixedInset0 = document.querySelectorAll('.fixed.inset-0');
        console.log(
          `[Bug Report] Found ${allFixedInset0.length} fixed inset-0 element(s) to check`
        );
        const dialogRect = dialogContent.getBoundingClientRect();

        allFixedInset0.forEach((el, index) => {
          const element = el as HTMLElement;
          const classList = element.classList;

          // Check if it matches backdrop pattern
          const isBackdropPattern =
            classList.contains('backdrop-blur') ||
            classList.contains('backdrop-blur-sm') ||
            element.className.includes('backdrop-blur') ||
            element.className.includes('bg-background/80') ||
            element.className.includes('bg-black/80');

          if (isBackdropPattern) {
            console.log(
              `[Bug Report] Checking fixed element ${index} (backdrop pattern):`,
              {
                tag: element.tagName,
                classes: element.className.substring(0, 100),
              }
            );

            const elementRect = element.getBoundingClientRect();
            // Check if this backdrop covers the dialog (geometric check)
            const coversDialog =
              elementRect.top <= dialogRect.top &&
              elementRect.left <= dialogRect.left &&
              elementRect.bottom >= dialogRect.bottom &&
              elementRect.right >= dialogRect.right;

            // Also check if it's a sibling of dialog or portal
            const isSiblingOfDialog =
              element.parentElement === dialogContent.parentElement;
            const isSiblingOfPortal =
              portal && element.parentElement === portal.parentElement;
            const isInPortal =
              portal && element.closest('[data-radix-portal]') === portal;

            // Only mark if it's definitely related to the bug report dialog
            // Priority: in portal > sibling of portal > sibling of dialog (only if no portal)
            // This prevents marking other dialogs' backdrops
            if (isInPortal) {
              element.setAttribute('data-bug-report-backdrop', 'true');
              console.log('✅ Marked bug report backdrop (in portal)');
              console.log(
                '✅ Verifying attribute:',
                element.hasAttribute('data-bug-report-backdrop')
              );
              backdropFound = true;
              return;
            } else if (isSiblingOfPortal) {
              element.setAttribute('data-bug-report-backdrop', 'true');
              console.log('✅ Marked bug report backdrop (sibling of portal)');
              console.log(
                '✅ Verifying attribute:',
                element.hasAttribute('data-bug-report-backdrop')
              );
              backdropFound = true;
              return;
            } else if (isSiblingOfDialog && !portal) {
              // Only mark if portal doesn't exist AND it covers the dialog
              // AND it's the closest backdrop to the dialog (check DOM order)
              if (coversDialog) {
                // Check if this backdrop is immediately before the dialog content in DOM
                // This ensures we only mark the backdrop that's directly associated with the bug report dialog
                const parent = dialogContent.parentElement;
                if (parent) {
                  const children = Array.from(parent.children);
                  const dialogIndex = children.indexOf(dialogContent);
                  const backdropIndex = children.indexOf(element);

                  // Backdrop should be right before the dialog (typical Radix UI structure)
                  // Or check if there's only one backdrop that matches
                  const isImmediatelyBefore = backdropIndex === dialogIndex - 1;
                  const allMatchingBackdrops = Array.from(
                    parent.children
                  ).filter((child) => {
                    const childEl = child as HTMLElement;
                    return (
                      childEl.classList.contains('fixed') &&
                      childEl.classList.contains('inset-0') &&
                      (childEl.classList.contains('backdrop-blur') ||
                        childEl.classList.contains('backdrop-blur-sm'))
                    );
                  });

                  // Only mark if it's immediately before OR if there's only one matching backdrop
                  if (
                    isImmediatelyBefore ||
                    allMatchingBackdrops.length === 1
                  ) {
                    element.setAttribute('data-bug-report-backdrop', 'true');
                    console.log(
                      '✅ Marked bug report backdrop (sibling and covers, no portal):',
                      {
                        isImmediatelyBefore,
                        matchingBackdropsCount: allMatchingBackdrops.length,
                      }
                    );
                    console.log(
                      '✅ Verifying attribute:',
                      element.hasAttribute('data-bug-report-backdrop')
                    );
                    backdropFound = true;
                    return;
                  }
                }
              }
            }
          }
        });
      }

      if (!backdropFound) {
        console.log('⚠️ Could not find backdrop during capture');
      }
    } else {
      console.log('⚠️ Dialog content not found during capture');
    }
  }

  // Find scrollable container and handle scrolling
  const { container, contentElement } = findScrollableContainer();
  const scrollContainer = container;
  const scrollTop = scrollContainer.scrollTop;
  const scrollHeight = scrollContainer.scrollHeight;
  const clientHeight = scrollContainer.clientHeight;
  const isScrollable = scrollHeight > clientHeight;

  console.log('[Bug Report] Scroll info:', {
    scrollTop,
    scrollHeight,
    clientHeight,
    isScrollable,
    container: scrollContainer.tagName,
    contentElement: contentElement?.tagName || 'none',
  });

  // If content is scrollable and user has scrolled, handle it
  if (isScrollable && scrollTop > 0) {
    try {
      // Step 1: Capture the full scrollable content
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, Math.min(devicePixelRatio * 2, 4));

      // Define viewport dimensions early
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Determine what element to capture - prefer contentElement if available
      const elementToCapture = contentElement || scrollContainer;
      // elementScrollHeight was used for debug logging but is no longer needed
      // const elementScrollHeight = elementToCapture.scrollHeight;

      // Step 2: Skip direct DOM manipulation and capture to avoid flashing
      // We used to expand the element here, but it caused visual glitches ("1 -> 2 -> 3" effect)
      // Now we go straight to creating the hidden iframe (Step 3) which handles the expansion safely off-screen
      console.log(
        '[Bug Report] Skipping in-place expansion to prevent visual flashes'
      );

      // Step 3: Create hidden iframe in DOM (completely invisible) to render HTML with CSS
      console.log(
        '[Bug Report] Step 3: Creating hidden iframe for HTML rendering'
      );

      // Clone the element to get its HTML
      const clonedElement = elementToCapture.cloneNode(true) as HTMLElement;
      // Expand to show all content
      clonedElement.style.height = 'auto';
      clonedElement.style.maxHeight = 'none';
      clonedElement.style.overflow = 'visible';
      clonedElement.scrollTop = 0;

      const elementHTML = clonedElement.outerHTML;

      // Collect all stylesheets from the current page
      const stylesheets: string[] = [];
      Array.from(document.styleSheets).forEach((stylesheet) => {
        try {
          if (stylesheet.href) {
            // External stylesheet
            stylesheets.push(
              `<link rel="stylesheet" href="${stylesheet.href}">`
            );
          } else if (stylesheet.ownerNode) {
            // Inline style tag
            const styleNode = stylesheet.ownerNode as HTMLStyleElement;
            if (styleNode.textContent) {
              stylesheets.push(`<style>${styleNode.textContent}</style>`);
            }
          }
        } catch (e) {
          // Cross-origin stylesheets might throw errors, skip them
          console.warn('[Bug Report] Could not access stylesheet:', e);
        }
      });

      // Also get all style tags from head
      const styleTags = Array.from(document.head.querySelectorAll('style'))
        .map((style) => `<style>${style.textContent}</style>`)
        .join('\n');

      // Get all link tags for stylesheets
      const linkTags = Array.from(
        document.head.querySelectorAll('link[rel="stylesheet"]')
      )
        .map((link) => link.outerHTML)
        .join('\n');

      // Calculate dimensions to match original content
      // This prevents reflow/scaling issues ("scale down" artifact)
      const captureWidth = elementToCapture.clientWidth || viewportWidth;
      const captureHeight = Math.max(
        elementToCapture.scrollHeight,
        viewportHeight
      );

      // Create a hidden iframe and append it to the DOM (completely invisible)
      // html2canvas needs elements to be renderable, so we'll position it off-screen
      // Apply ALL hiding styles BEFORE appending to prevent any flash
      const iframe = document.createElement('iframe');
      // Set styles before appending - position off-screen but renderable for html2canvas
      // Removed transform: scale(0.001) as it causes rendering issues
      iframe.style.cssText = `
        position: fixed !important;
        top: -99999px !important;
        left: -99999px !important;
        width: ${captureWidth}px !important;
        height: ${captureHeight}px !important;
        border: none !important;
        opacity: 0 !important;
        pointer-events: none !important;
        visibility: hidden !important;
        z-index: -999999 !important;
        clip: rect(0, 0, 0, 0) !important;
        clip-path: inset(100%) !important;
      `;
      iframe.setAttribute('data-bug-report-hidden-iframe', 'true');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      // Append to DOM - styles are already applied so it won't be visible
      document.body.appendChild(iframe);

      // Force a reflow to ensure styles are applied before any rendering
      void iframe.offsetHeight;

      // Double-check it's hidden with requestAnimationFrame
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Ensure it's still hidden
            iframe.style.cssText = `
              position: fixed !important;
              top: -99999px !important;
              left: -99999px !important;
              width: ${captureWidth}px !important;
              height: ${captureHeight}px !important;
              border: none !important;
              opacity: 0 !important;
              pointer-events: none !important;
              visibility: hidden !important;
              z-index: -999999 !important;
              clip: rect(0, 0, 0, 0) !important;
              clip-path: inset(100%) !important;
            `;
            resolve();
          });
        });
      });

      // Wait for the iframe to load, then capture it as an image
      const htmlPreviewImageSrc = await new Promise<string>(
        (resolve, reject) => {
          // Use async IIFE inside the Promise
          (async () => {
            try {
              // Wait for iframe to be ready
              await new Promise<void>((resolveReady) => {
                const checkReady = () => {
                  const iframeDoc = iframe.contentDocument;
                  if (iframeDoc && iframeDoc.readyState === 'complete') {
                    resolveReady();
                  } else {
                    setTimeout(checkReady, 50);
                  }
                };
                iframe.onload = () => resolveReady();
                // Start checking immediately
                checkReady();
                // Fallback timeout
                setTimeout(() => resolveReady(), 2000);
              });

              const iframeDoc = iframe.contentDocument;
              if (!iframeDoc) {
                iframe.remove();
                reject(new Error('Iframe document not available'));
                return;
              }

              // Write HTML content to iframe
              iframeDoc.open();
              iframeDoc.write(`
                <!DOCTYPE html>
                <html>
                  <head>
                    <title>Captured Content</title>
                    <meta charset="utf-8">
                    ${linkTags}
                    ${styleTags}
                    <style>
                      body {
                        margin: 0;
                        padding: 0;
                        background: white;
                        overflow: visible;
                      }
                      #htmlContainer {
                        background: white;
                        padding: 0;
                        margin: 0;
                        max-width: 100%;
                        overflow: visible;
                        border: none;
                        box-shadow: none;
                      }
                      /* Hide scrollbars */
                      ::-webkit-scrollbar {
                        display: none;
                      }
                      /* Ensure sensitive data blur is preserved */
                      .sensitive-data-blurred {
                        filter: blur(8px) !important;
                        -webkit-filter: blur(8px) !important;
                        color: transparent !important;
                        text-shadow: 0 0 8px rgba(0,0,0,0.5) !important;
                        user-select: none !important;
                      }
                      [data-blurred="true"] {
                        filter: blur(8px) !important;
                        -webkit-filter: blur(8px) !important;
                        color: transparent !important;
                        text-shadow: 0 0 8px rgba(0,0,0,0.5) !important;
                      }
                    </style>
                  </head>
                  <body>
                    <div id="htmlContainer">${elementHTML}</div>
                    <script>
                      (function() {
                        const container = document.getElementById('htmlContainer');
                        const insertedElement = container.querySelector('${elementToCapture.tagName.toLowerCase()}') || container.firstElementChild;
                        if (insertedElement) {
                          insertedElement.style.height = 'auto';
                          insertedElement.style.maxHeight = 'none';
                          insertedElement.style.overflow = 'visible';
                          insertedElement.style.margin = '0';
                          if (insertedElement.scrollTop !== undefined) {
                            insertedElement.scrollTop = 0;
                          }
                        }
                        
                        // Reinforce sensitive data blurring
                        const blurredElements = container.querySelectorAll('[data-blurred="true"], .sensitive-data-blurred');
                        blurredElements.forEach(el => {
                          el.style.filter = 'blur(8px)';
                          el.style.webkitFilter = 'blur(8px)';
                          el.style.color = 'transparent';
                          el.style.textShadow = '0 0 8px rgba(0,0,0,0.5)';
                        });
                      })();
                    </script>
                  </body>
                </html>
              `);
              iframeDoc.close();

              // Wait for iframe content to fully load after writing
              await new Promise<void>((resolveLoad) => {
                const checkLoad = () => {
                  if (
                    iframeDoc.readyState === 'complete' &&
                    iframeDoc.body &&
                    iframeDoc.body.children.length > 0
                  ) {
                    resolveLoad();
                  } else {
                    setTimeout(checkLoad, 50);
                  }
                };
                // Start checking immediately
                checkLoad();
                // Also listen for load event
                iframe.onload = () => resolveLoad();
                // Fallback timeout
                setTimeout(() => resolveLoad(), 2000);
              });

              // Wait for stylesheets to load
              await new Promise((resolve) => setTimeout(resolve, 1000));

              console.log(
                '[Bug Report] Iframe HTML loaded, capturing entire iframe body'
              );

              if (iframeDoc.body) {
                // Capture the entire iframe body
                const htmlPreviewCanvas = await html2canvas(iframeDoc.body, {
                  useCORS: true,
                  allowTaint: false,
                  logging: false,
                  scale: scale,
                  backgroundColor: '#ffffff',
                  removeContainer: false,
                  imageTimeout: 15000,
                  foreignObjectRendering: true,
                  windowWidth: captureWidth,
                  windowHeight: captureHeight,
                });

                const imageSrc = htmlPreviewCanvas.toDataURL('image/png', 1.0);

                // Remove the iframe from DOM
                iframe.remove();

                resolve(imageSrc);
              } else {
                iframe.remove();
                reject(new Error('Iframe body not found'));
              }
            } catch (error) {
              // Ensure iframe is removed even on error
              if (iframe.parentNode) {
                iframe.remove();
              }
              reject(error);
            }
          })();
        }
      );

      // Step 4: Replace scrollable container content with HTML preview image
      // viewportWidth and viewportHeight are already defined above

      // Use the scrollTop from the element we captured
      const elementScrollTop = contentElement
        ? contentElement.scrollTop
        : scrollTop;

      // Create a completely separate hidden container for the image replacement
      // This way the original container stays untouched and hidden
      // Apply styles BEFORE creating to prevent any flash
      const hiddenContainer = document.createElement('div');
      hiddenContainer.style.cssText = `
        position: fixed !important;
        top: -99999px !important;
        left: -99999px !important;
        width: ${captureWidth}px !important;
        height: ${viewportHeight}px !important;
        opacity: 0 !important;
        visibility: hidden !important;
        pointer-events: none !important;
        z-index: -999999 !important;
        clip: rect(0, 0, 0, 0) !important;
        clip-path: inset(100%) !important;
        overflow: hidden !important;
        display: block !important;
      `;
      hiddenContainer.setAttribute('data-bug-report-hidden-container', 'true');
      hiddenContainer.setAttribute('aria-hidden', 'true');

      // Append to DOM with styles already applied
      document.body.appendChild(hiddenContainer);

      // Force immediate reflow and ensure it's hidden
      void hiddenContainer.offsetHeight;

      // Double-check with requestAnimationFrame to prevent any flash
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Re-apply hiding styles to ensure they're active
            hiddenContainer.style.cssText = `
              position: fixed !important;
              top: -99999px !important;
              left: -99999px !important;
              width: ${captureWidth}px !important;
              height: ${viewportHeight}px !important;
              opacity: 0 !important;
              visibility: hidden !important;
              pointer-events: none !important;
              z-index: -999999 !important;
              clip: rect(0, 0, 0, 0) !important;
              clip-path: inset(100%) !important;
              overflow: hidden !important;
              display: block !important;
            `;
            resolve();
          });
        });
      });

      // Create image element with HTML preview image
      // The container is already hidden, so the image won't be visible even if it's "visible" inside
      const img = document.createElement('img');
      img.style.display = 'block';
      img.style.position = 'absolute';
      img.style.left = '0';
      img.style.top = '0';
      img.style.width = `${captureWidth}px`;
      img.style.height = 'auto';

      // Set hidden container to relative positioning and add image
      hiddenContainer.style.position = 'relative';
      hiddenContainer.appendChild(img);

      // Set src after image is in hidden container
      img.src = htmlPreviewImageSrc;

      // Wait for image to load and measure its displayed size
      await new Promise<void>((resolveImg) => {
        if (img.complete) {
          resolveImg();
        } else {
          img.onload = () => resolveImg();
          img.onerror = () => resolveImg(); // Continue even if image fails to load
        }
      });

      // Small delay to ensure rendering and layout calculation
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Measure the actual displayed height of the image
      const displayedImageHeight = img.clientHeight || img.offsetHeight;

      // Get the full scroll height of the original content
      const originalScrollHeight = elementToCapture.scrollHeight;

      // Calculate the scroll ratio: scrollTop / scrollHeight (at 1x scale)
      // This ratio represents how far down the user scrolled relative to the TOTAL content height
      const scrollRatio =
        originalScrollHeight > 0 ? elementScrollTop / originalScrollHeight : 0;

      // Apply the same ratio to the displayed image height
      // Formula: (scrollTop / scrollHeight) * displayedImageHeight
      const realTopOffset = scrollRatio * displayedImageHeight;

      console.log('[Bug Report] Top offset calculation (ratio-based):', {
        scrollTop: elementScrollTop,
        originalScrollHeight: originalScrollHeight,
        scrollRatio: scrollRatio,
        displayedImageHeight: displayedImageHeight,
        realTopOffset: realTopOffset,
        formula: `(${elementScrollTop} / ${originalScrollHeight}) × ${displayedImageHeight.toFixed(
          2
        )} = ${realTopOffset.toFixed(2)}`,
      });

      // Position the image: move it up by the calculated real top offset
      img.style.top = `-${realTopOffset}px`;

      // Force a reflow to ensure styles are applied
      void hiddenContainer.offsetHeight;

      // Step 5: Capture the final screenshot with replaced content
      // We'll use onclone to replace the scrollContainer with our hiddenContainer's content
      console.log(
        '[Bug Report] Step 5: Capturing final screenshot with replaced content'
      );
      const finalScreenshot = await new Promise<string>(
        (resolveFinal, rejectFinal) => {
          const devicePixelRatio = window.devicePixelRatio || 1;
          const finalScale = Math.max(3, Math.min(devicePixelRatio * 2, 4));

          html2canvas(document.body, {
            useCORS: true,
            allowTaint: false,
            logging: false,
            scale: finalScale,
            windowWidth: viewportWidth,
            windowHeight: viewportHeight,
            backgroundColor: '#ffffff',
            removeContainer: false,
            imageTimeout: 15000,
            foreignObjectRendering: true,
            ignoreElements: (element) => {
              // Ignore the hidden container itself
              if (element.hasAttribute('data-bug-report-hidden-container')) {
                return true;
              }
              return isBugReportElement(element);
            },
            onclone: (clonedDoc) => {
              handleBugReportDialogHiding(clonedDoc);

              // Find the scrollContainer in the cloned document
              const clonedScrollContainer = clonedDoc.querySelector(
                scrollContainer.tagName.toLowerCase()
              ) as HTMLElement | null;

              if (clonedScrollContainer && hiddenContainer.firstElementChild) {
                // Get the original container's width to preserve it (prevent scaling down)
                const originalContainerWidth =
                  scrollContainer.clientWidth ||
                  scrollContainer.offsetWidth ||
                  viewportWidth;

                // Replace the cloned scrollContainer's content with our image
                clonedScrollContainer.innerHTML = '';
                clonedScrollContainer.style.position = 'relative';
                clonedScrollContainer.style.overflow = 'hidden';
                clonedScrollContainer.style.height = `${viewportHeight}px`;
                clonedScrollContainer.style.width = `${originalContainerWidth}px`;
                clonedScrollContainer.style.minWidth = `${originalContainerWidth}px`;
                clonedScrollContainer.style.maxWidth = 'none';
                clonedScrollContainer.style.boxSizing = 'border-box';

                // Clone the image from hiddenContainer and ensure correct styles
                const clonedImg = hiddenContainer.firstElementChild.cloneNode(
                  true
                ) as HTMLImageElement;
                // Ensure the cloned image has the correct styles (no transform, correct positioning)
                clonedImg.style.display = 'block';
                clonedImg.style.position = 'absolute';
                clonedImg.style.left = '0';
                clonedImg.style.top = `-${realTopOffset}px`;
                clonedImg.style.width = `${captureWidth}px`;
                clonedImg.style.height = 'auto';
                clonedImg.style.transform = '';
                clonedImg.style.zIndex = '999999';
                clonedScrollContainer.appendChild(clonedImg);
              }
            },
          })
            .then((canvas) => {
              const dataUrl = canvas.toDataURL('image/png', 1.0);
              resolveFinal(dataUrl);
            })
            .catch((error) => {
              rejectFinal(error);
            });
        }
      );

      // Cleanup: Remove the hidden container
      hiddenContainer.remove();

      return finalScreenshot;
    } catch (error) {
      console.error('[Bug Report] Error handling scrollable content:', error);
      // Fall through to normal capture
    }
  }

  // Normal capture for non-scrollable or non-scrolled content
  // Use html2canvas to capture screenshot
  // Use higher scale for better quality (3-4x resolution)
  // Higher scale = better quality but larger file size
  const devicePixelRatio = window.devicePixelRatio || 1;
  // Use 3x scale minimum, or 2x device pixel ratio if higher, capped at 4x
  const scale = Math.max(3, Math.min(devicePixelRatio * 2, 4));

  try {
    const canvas: HTMLCanvasElement = await html2canvas(document.body, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      scale: scale, // High resolution for better quality
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      backgroundColor: '#ffffff',
      removeContainer: false,
      imageTimeout: 15000,
      foreignObjectRendering: true, // Better rendering for complex content
      ignoreElements: (element) => {
        // Ignore elements that are part of the bug report dialog
        const shouldIgnore = isBugReportElement(element);
        if (shouldIgnore) {
          console.log('[Bug Report] Ignoring element:', {
            tag: element.tagName,
            classes: element.className,
            hasOverlayAttr: element.hasAttribute('data-radix-dialog-overlay'),
            hasBugReportAttr: element.hasAttribute('data-bug-report-dialog'),
          });
        }
        return shouldIgnore;
      },
      onclone: (clonedDoc) => {
        handleBugReportDialogHiding(clonedDoc);
      },
    });

    // Export at highest quality
    // PNG format preserves quality better than JPEG
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    console.log('📸 html2canvas completed, data URL length:', dataUrl.length);
    if (!dataUrl || dataUrl.length < 100) {
      console.error('❌ Screenshot data URL is too short, using fallback');
      return createFallbackScreenshot();
    }
    return dataUrl;
  } catch (error) {
    console.error('❌ html2canvas error:', error);
    // Fallback to a simple canvas
    return createFallbackScreenshot();
  }
}

/**
 * Capture screenshot and store it for the bug report dialog
 * This runs BEFORE the dialog opens and stores the result in window.__preCapturedScreenshot
 */
export async function captureScreenshotWithPreview(): Promise<string> {
  console.log('[Bug Report] Starting capture before dialog opens');
  const screenshot = await captureScreenshotInternal(false);
  // Store the screenshot for the dialog to use
  (window as any).__preCapturedScreenshot = screenshot;
  return screenshot;
}

/**
 * Capture screenshot of the current page
 * Requires html2canvas library to be loaded
 * Note: Sensitive data blurring should be activated before calling this function
 */
export async function captureScreenshot(): Promise<string> {
  return captureScreenshotInternal(true);
}

function createFallbackScreenshot(): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;

  // Draw white background
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Draw message with larger, more visible text
  ctx.fillStyle = '#333';
  ctx.font = 'bold 24px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  const message = 'Screenshot capture requires html2canvas library';
  const x = canvas.width / 2;
  const y = canvas.height / 2;

  // Add text shadow for better visibility
  ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;

  ctx.fillText(message, x, y);

  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;

  return canvas.toDataURL('image/png');
}

/**
 * Get current URL
 */
export function getCurrentUrl(): string {
  try {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.href;
  } catch (error) {
    console.error('Error getting current URL:', error);
    return 'Error retrieving URL';
  }
}

/**
 * Get user agent and browser info
 */
export function getBrowserInfo(): Record<string, string> {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {};
    }

    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width.toString(),
      screenHeight: window.screen.height.toString(),
      windowWidth: window.innerWidth.toString(),
      windowHeight: window.innerHeight.toString(),
    };
  } catch (error) {
    console.error('Error getting browser info:', error);
    return {
      userAgent: 'Error retrieving',
      platform: 'Error retrieving',
      language: 'Error retrieving',
    };
  }
}
